import React from 'react'

import TopContent from './Common/TopContent'
import LEBookingTag from '../../Tripkit/Common/LEBookingTag'
import { GooglePlaceRating } from '../GooglePlaceRating/GooglePlaceRating'
import HeadingFullPrint from '../util/HeadingFullPrint'

import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import LineMapMarkerIcon from 'components/Luxkit/Icons/line/LineMapMarkerIcon'
import BodyTextBlock from 'components/Luxkit/TextBlocks/BodyTextBlock'
import BodyText from 'components/Luxkit/Typography/BodyText'
import NoPrint from 'components/utils/NoPrint'
import PrintOnly from 'components/utils/PrintOnly'
import { EmptyArray, nonNullable } from 'lib/arrayUtils'
import { memoWithNestedProps } from 'lib/memoWithNestedProps'
import { SAVE_CURATED_TRIP_BUTTON_CLASS } from 'tripPlanner/components/Buttons/SaveCuratedTripItemButton'
import SaveTripItemButton from 'tripPlanner/components/Buttons/SaveTripItemButton'
import BaseItemCard from 'tripPlanner/components/ItemCard/BaseItemCard'
import ContentWrapper from 'tripPlanner/components/ItemCard/Common/ContentWrapper'
import { ItemImageContainer } from 'tripPlanner/components/ItemCard/ItemImageContainer'
import { ItemMetadata } from 'tripPlanner/models'
import { TripItem } from 'tripPlanner/types/tripItem'
import { isLeBooking } from 'tripPlanner/utils'

interface Props {
  item: TripItem
  /*
    Different item types have different logic for what exactly is shown for each of these fields.
    Hence they are passed in as props rather than extracted from the item within this component.
  */
  name: string
  /** URL of external image */
  imgSrc?: string
  /** ID of image from svc-image */
  imgId?: string
  placeId?: string
  locationDescription?: React.ReactNode
  descriptors?: Array<string | undefined>
  /** Alternate `descriptors` array to use for print view */
  printDescriptors?: Array<string | undefined>
  address?: string
  confirmationCode?: string
  confirmationCodeLabel?: string
  imageOverlayContent?: React.ReactNode
  bottomContent?: React.ReactNode
  /** Disables click handler, link and menu */
  nonInteractable?: boolean
  onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void
}

function ItineraryItemCard(props: Props) {
  const {
    item,
    name,
    imgSrc,
    imgId,
    placeId,
    locationDescription,
    descriptors = EmptyArray,
    printDescriptors = EmptyArray,
    address,
    confirmationCode,
    confirmationCodeLabel,
    imageOverlayContent,
    bottomContent,
    nonInteractable = false,
    onClick,
  } = props
  const { type } = item

  const descriptorsString = nonNullable(descriptors).join(' • ')
  const printDescriptorsString = nonNullable(printDescriptors).join(' • ')
  // If we have different descriptors for print view, only show the normal ones in non-print view
  const DescriptorsWrapper = printDescriptorsString ? NoPrint : React.Fragment
  return (
    <BaseItemCard
      item={item}
      nonInteractable={nonInteractable}
      onClick={onClick}
    >
      <ItemImageContainer
        item={item}
        name={name}
        imgSrc={imgSrc}
        imgId={imgId}
        placeId={placeId}
        imageOverlayContent={imageOverlayContent}
      />
      <ContentWrapper>
        <TopContent item={item} nonInteractable={nonInteractable}>
          {ItemMetadata[type].singularName}
        </TopContent>

        <VerticalSpacer gap={4}>
          {locationDescription && (
            <BodyText variant="small" lineClamp={2}>
              {locationDescription}
            </BodyText>
          )}
          <HeadingFullPrint variant="heading6" lineClamp={2}>
            {name}
          </HeadingFullPrint>
          <VerticalSpacer gap={8}>
            {placeId && <GooglePlaceRating placeId={placeId} size="S" />}
            <div>
              {confirmationCode && (
                <PrintOnly>
                  <BodyText variant="medium">
                    {confirmationCodeLabel && (
                      <BodyText as="span" variant="medium" weight="bold">
                        {confirmationCodeLabel}:{' '}
                      </BodyText>
                    )}
                    {confirmationCode}
                  </BodyText>
                </PrintOnly>
              )}
              {descriptorsString && (
                <DescriptorsWrapper>
                  <BodyText variant="medium">{descriptorsString}</BodyText>
                </DescriptorsWrapper>
              )}
              {printDescriptorsString && (
                <PrintOnly>
                  <BodyText variant="medium">{printDescriptorsString}</BodyText>
                </PrintOnly>
              )}
            </div>
          </VerticalSpacer>
        </VerticalSpacer>
        {isLeBooking(item) && <LEBookingTag />}
        {bottomContent}
        <SaveTripItemButton
          className={SAVE_CURATED_TRIP_BUTTON_CLASS}
          item={item}
        />
        {address && (
          <PrintOnly>
            <BodyTextBlock variant="small" startIcon={<LineMapMarkerIcon />}>
              {address}
            </BodyTextBlock>
          </PrintOnly>
        )}
        {item.notes && (
          <PrintOnly>
            <BodyText variant="small" weight="normal" wrap="pre-wrap">
              {item.notes}
            </BodyText>
          </PrintOnly>
        )}
      </ContentWrapper>
    </BaseItemCard>
  )
}

export default memoWithNestedProps(ItineraryItemCard, [
  'descriptors',
  'printDescriptors',
])
